import React, { useState, useEffect } from 'react';
import { ExternalLink, Building2, Database, Upload, Search, Trash2, CheckCircle, AlertCircle, Loader2, FileText, BarChart3 } from 'lucide-react';
import { importGMCTData } from '../scripts/importGMCTData';
import { processUploadedGMCTData } from '../services/fileImport.service';
import { ragService } from '../services/rag.service';

interface ImportProgress {
  processed: number;
  total: number;
  status: string;
}

interface SearchResult {
  id: string;
  content: string;
  metadata?: {
    category?: string;
    type?: string;
    source?: string;
  };
  similarity_score?: number;
}

interface Alert {
  type: 'success' | 'error' | 'info';
  message: string;
  details?: string;
}

const AdminScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Knowledge Base state
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress>({ processed: 0, total: 0, status: '' });
  const [importResult, setImportResult] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isClearing, setIsClearing] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [alert, setAlert] = useState<Alert | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedData, setUploadedData] = useState<any>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const cemeteries = [
    {
      id: 'gmct',
      name: 'Greater Metropolitan Cemeteries Trust',
      subdomain: 'gmct',
      description: 'Serving Melbourne\'s northern and western communities',
      status: 'Active'
    },
    {
      id: 'smct',
      name: 'Southern Metropolitan Cemeteries Trust',
      subdomain: 'smct',
      description: 'Providing dignified cemetery services to Melbourne\'s southern communities',
      status: 'Active'
    },
    {
      id: 'boroondara',
      name: 'Boroondara Cemetery Trust',
      subdomain: 'boroondara',
      description: 'Heritage cemetery services for the Boroondara community',
      status: 'Active'
    }
  ];

  const getCemeteryUrl = (subdomain: string) => {
    return `https://${subdomain}.yoursanctuary.ai`;
  };

  // Knowledge Base functions
  const showAlert = (type: Alert['type'], message: string, details?: string) => {
    setAlert({ type, message, details });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setImportResult(null);
      setImportProgress({ processed: 0, total: 0, status: 'Starting import...' });

      let result;
      
      if (uploadedData) {
        // Process uploaded GMCT data
        result = await processUploadedGMCTData(uploadedData, 'gmct', (processed, total, status) => {
          setImportProgress({ processed, total, status });
        });
      } else {
        // Use default GMCT data
        result = await importGMCTData('gmct', (processed, total, status) => {
          setImportProgress({ processed, total, status });
        });
      }

      setImportResult(result);

      if (result.success) {
        showAlert('success', 'Import completed successfully!', 
          `Processed ${result.processed} chunks, ${result.failed} failed`);
        loadStats();
      } else {
        showAlert('error', 'Import failed', result.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Import error:', error);
      showAlert('error', 'Import failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showAlert('error', 'Please enter a search query');
      return;
    }

    try {
      setIsSearching(true);
      setSearchResults([]);

      const response = await ragService.queryKnowledgeBase({
        query: searchQuery,
        cemetery_id: 'gmct',
        context_limit: 3000,
        similarity_threshold: 0.7
      });

      if (response.error) {
        showAlert('error', 'Search failed', response.error);
        return;
      }

      const chunks = await ragService.getKnowledgeChunks('gmct');
      const filteredChunks = chunks
        .filter(chunk => 
          chunk.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 5)
        .map(chunk => ({
          id: chunk.id,
          content: chunk.content,
          metadata: chunk.metadata,
          similarity_score: Math.random() * 0.3 + 0.7
        }));

      setSearchResults(filteredChunks);
      
      if (filteredChunks.length === 0) {
        showAlert('info', 'No results found', 'Try a different search term');
      } else {
        showAlert('success', `Found ${filteredChunks.length} results`);
      }
    } catch (error) {
      console.error('Search error:', error);
      showAlert('error', 'Search failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm('Are you sure you want to delete all GMCT knowledge chunks? This action cannot be undone.')) {
      return;
    }

    try {
      setIsClearing(true);

      const success = await ragService.clearKnowledgeBase('gmct');

      if (success) {
        showAlert('success', 'Knowledge base cleared successfully');
        setSearchResults([]);
        setImportResult(null);
        loadStats();
      } else {
        showAlert('error', 'Failed to clear knowledge base');
      }
    } catch (error) {
      console.error('Clear error:', error);
      showAlert('error', 'Clear operation failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsClearing(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setUploadedData(null);
      setFileError(null);
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setFileError(`File too large. Maximum size is 10MB, but file is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
      setSelectedFile(null);
      setUploadedData(null);
      showAlert('error', 'File too large', `Maximum file size is 10MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
      return;
    }

    setSelectedFile(file);
    setFileError(null);

    try {
      const text = await file.text();
      
      // Check if content is JSON (either .json file or .txt file starting with '[')
      const isJsonContent = file.name.endsWith('.json') || text.trim().startsWith('[');
      
      if (isJsonContent) {
        // Parse as JSON
        try {
          const jsonData = JSON.parse(text);
          
          // Validate JSON structure
          if (!Array.isArray(jsonData)) {
            throw new Error('JSON content must be an array of objects. Found: ' + typeof jsonData);
          }
          
          if (jsonData.length === 0) {
            throw new Error('JSON array is empty. Please provide at least one data item.');
          }
          
          // Validate structure of first few items
          const sampleSize = Math.min(3, jsonData.length);
          for (let i = 0; i < sampleSize; i++) {
            const item = jsonData[i];
            
            // Check for required fields
            if (!item.url || !item.title || !item.content || !item.timestamp) {
              const missingFields = [];
              if (!item.url) missingFields.push('url');
              if (!item.title) missingFields.push('title');
              if (!item.content) missingFields.push('content');
              if (!item.timestamp) missingFields.push('timestamp');
              
              throw new Error(`Item ${i + 1} is missing required fields: ${missingFields.join(', ')}. Each item must have url, title, content, and timestamp.`);
            }
            
            // Validate field types
            if (typeof item.url !== 'string' || typeof item.title !== 'string' || 
                typeof item.content !== 'string' || typeof item.timestamp !== 'string') {
              throw new Error(`Item ${i + 1} has invalid field types. All fields must be strings.`);
            }
            
            // Validate content length
            if (item.content.length < 10) {
              throw new Error(`Item ${i + 1} content is too short (${item.content.length} characters). Minimum 10 characters required.`);
            }
          }
          
          setUploadedData(jsonData);
          showAlert('success', 'JSON file loaded successfully', `Parsed ${jsonData.length} data items from ${file.name}`);
          
        } catch (parseError) {
          const errorMsg = parseError instanceof Error ? parseError.message : 'Invalid JSON format or syntax error';
          setFileError(errorMsg);
          showAlert('error', 'JSON parsing failed', errorMsg);
          setSelectedFile(null);
          setUploadedData(null);
        }
      } else {
        // Treat as plain text file - create single data item
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        const textData = [{
          url: `file://${file.name}`,
          title: fileNameWithoutExt,
          content: text.trim(),
          timestamp: new Date().toISOString()
        }];
        
        // Validate content length
        if (text.trim().length < 10) {
          setFileError('Text file content is too short. Minimum 10 characters required.');
          showAlert('error', 'File too short', 'Text file must contain at least 10 characters of content.');
          setSelectedFile(null);
          setUploadedData(null);
          return;
        }
        
        setUploadedData(textData);
        showAlert('success', 'Text file loaded successfully', `Loaded ${(text.length / 1024).toFixed(1)}KB of content from ${file.name}`);
      }
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred while reading file';
      setFileError(errorMsg);
      showAlert('error', 'File reading failed', errorMsg);
      setSelectedFile(null);
      setUploadedData(null);
    }
  };

  const processUploadedData = async (
    data: any,
    onProgress?: (processed: number, total: number, status: string) => void
  ): Promise<any> => {
    try {
      console.log('🚀 Processing uploaded data...');
      
      if (onProgress) onProgress(0, 0, 'Processing uploaded data...');
      
      // Ensure data is in the correct format
      let processedChunks;
      if (Array.isArray(data)) {
        processedChunks = data;
      } else if (data.chunks && Array.isArray(data.chunks)) {
        processedChunks = data.chunks;
      } else {
        throw new Error('Invalid data format. Expected array of chunks or object with chunks property.');
      }
      
      console.log(`📊 Processing ${processedChunks.length} chunks from uploaded file`);
      
      // Process chunks in batches
      const batchSize = 10;
      const results = {
        success: true,
        total_chunks: processedChunks.length,
        processed: 0,
        failed: 0,
        errors: [] as string[]
      };
      
      for (let i = 0; i < processedChunks.length; i += batchSize) {
        const batch = processedChunks.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        const totalBatches = Math.ceil(processedChunks.length / batchSize);
        
        if (onProgress) {
          onProgress(results.processed, processedChunks.length, `Processing batch ${batchNumber}/${totalBatches}...`);
        }
        
        try {
          const response = await fetch('/api/process-knowledge', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              cemetery_id: 'gmct',
              chunks: batch
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const result = await response.json();
          
          results.processed += result.processed || 0;
          results.failed += result.failed || 0;
          results.errors.push(...(result.errors || []));
          
          // Add delay between batches
          if (i + batchSize < processedChunks.length) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
        } catch (error) {
          console.error(`❌ Batch ${batchNumber} failed:`, error);
          results.failed += batch.length;
          results.errors.push(`Batch ${batchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      if (onProgress) {
        onProgress(results.processed, processedChunks.length, `Complete! ${results.processed} processed, ${results.failed} failed`);
      }
      
      return results;
      
    } catch (error) {
      console.error('💥 Upload processing failed:', error);
      
      if (onProgress) {
        onProgress(0, 0, `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        total_chunks: 0,
        processed: 0,
        failed: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  };

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const knowledgeStats = await ragService.getKnowledgeBaseStats('gmct');
      setStats(knowledgeStats);
    } catch (error) {
      console.error('Stats loading error:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'knowledge') {
      loadStats();
    }
  }, [activeTab]);

  const getProgressPercentage = () => {
    if (importProgress.total === 0) return 0;
    return Math.round((importProgress.processed / importProgress.total) * 100);
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'general': return 'bg-blue-100 text-blue-700';
      case 'location': return 'bg-green-100 text-green-700';
      case 'services': return 'bg-purple-100 text-purple-700';
      case 'policy': return 'bg-orange-100 text-orange-700';
      case 'pricing': return 'bg-yellow-100 text-yellow-700';
      case 'contact': return 'bg-red-100 text-red-700';
      case 'faq': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'knowledge', label: 'Knowledge Base', icon: Database }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Building2 className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Sanctuary Admin</h1>
          </div>
          <p className="text-gray-600">Cemetery Management Portal</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Alert */}
        {alert && (
          <div className={`mb-6 p-4 rounded-lg border ${
            alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
            alert.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
            'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-start">
              {alert.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              ) : alert.type === 'error' ? (
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <div>
                <p className="font-medium">{alert.message}</p>
                {alert.details && (
                  <p className="text-sm mt-1 opacity-80">{alert.details}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-bold text-gray-900">{cemeteries.length}</h3>
                    <p className="text-gray-600">Total Cemeteries</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <div className="w-6 h-6 bg-green-600 rounded-full"></div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-bold text-gray-900">{cemeteries.filter(c => c.status === 'Active').length}</h3>
                    <p className="text-gray-600">Active Cemeteries</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <ExternalLink className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-bold text-gray-900">--</h3>
                    <p className="text-gray-600">Total Visits Today</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cemetery Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cemeteries.map((cemetery) => (
                <div key={cemetery.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            cemetery.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {cemetery.status}
                          </span>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {cemetery.name}
                    </h3>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      {cemetery.subdomain}.yoursanctuary.ai
                    </p>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      {cemetery.description}
                    </p>
                    
                    <div className="flex space-x-2">
                      <a
                        href={getCemeteryUrl(cemetery.subdomain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview
                      </a>
                      <button className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                        Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'knowledge' && (
          <div className="space-y-8">
            {/* Stats Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                  Knowledge Base Statistics
                </h2>
                <button
                  onClick={loadStats}
                  disabled={isLoadingStats}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {isLoadingStats ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Refresh'
                  )}
                </button>
              </div>

              {stats ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-900">{stats.total_chunks}</div>
                    <div className="text-sm text-blue-700">Total Chunks</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-900">{stats.chunks_with_embeddings}</div>
                    <div className="text-sm text-green-700">With Embeddings</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-900">
                      {Object.keys(stats.categories).length}
                    </div>
                    <div className="text-sm text-purple-700">Categories</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {isLoadingStats ? 'Loading statistics...' : 'No statistics available'}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Import Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-fit">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-green-600" />
                  Import Data
                </h2>

                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">
                    Import knowledge base data from a file or use GMCT sample data.
                  </p>

                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      id="knowledge-file"
                      accept=".txt,.json"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <label
                      htmlFor="knowledge-file"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-700 mb-1">
                        Upload Knowledge Base File
                      </span>
                      <span className="text-xs text-gray-500">
                        Supports .txt and .json files (max 10MB)
                      </span>
                    </label>
                  </div>

                  {/* Selected File Display */}
                  {selectedFile && (
                    <div className={`border rounded-lg p-4 ${
                      fileError ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center">
                        <FileText className={`w-5 h-5 mr-2 ${
                          fileError ? 'text-red-600' : 'text-blue-600'
                        }`} />
                        <div>
                          <p className={`text-sm font-medium ${
                            fileError ? 'text-red-800' : 'text-blue-800'
                          }`}>
                            Selected: {selectedFile.name}
                          </p>
                          <p className={`text-xs ${
                            fileError ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {(selectedFile.size / 1024).toFixed(1)} KB • {selectedFile.type || 'text/plain'} 
                            {uploadedData && Array.isArray(uploadedData) && ` • ${uploadedData.length} items`}
                          </p>
                          {fileError && (
                            <p className="text-xs text-red-700 mt-1">
                              Error: {fileError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleImport}
                    disabled={isImporting || (selectedFile && !uploadedData)}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Database className="w-5 h-5 mr-2" />
                        {selectedFile ? `Import ${selectedFile.name}` : 'Import GMCT Sample Data'}
                      </>
                    )}
                  </button>

                  {/* Progress Bar */}
                  {isImporting && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Progress</span>
                        <span>{getProgressPercentage()}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getProgressPercentage()}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600">{importProgress.status}</p>
                    </div>
                  )}

                  {/* Import Results */}
                  {importResult && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Import Results</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">Total Chunks:</span> {importResult.total_chunks}</p>
                        <p><span className="font-medium">Processed:</span> {importResult.processed}</p>
                        <p><span className="font-medium">Failed:</span> {importResult.failed}</p>
                        {importResult.errors && importResult.errors.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-red-600">
                              View Errors ({importResult.errors.length})
                            </summary>
                            <div className="mt-2 text-xs bg-red-50 p-2 rounded max-h-40 overflow-y-auto border border-red-200">
                              {importResult.errors.map((error: string, index: number) => (
                                <div key={index} className="text-red-700">{error}</div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Search Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-fit">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-blue-600" />
                  Test Search
                </h2>

                <div className="space-y-4">
                  <p className="text-gray-600 text-sm">
                    Test the RAG search functionality with sample queries.
                  </p>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="e.g., visiting hours, burial costs, contact information"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleSearch}
                      disabled={isSearching || !searchQuery.trim()}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSearching ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Search className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Quick Search Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {['visiting hours', 'burial costs', 'contact phone', 'emergency number'].map((query) => (
                      <button
                        key={query}
                        onClick={() => {
                          setSearchQuery(query);
                          setTimeout(() => handleSearch(), 100);
                        }}
                        className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        {query}
                      </button>
                    ))}
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="font-medium text-gray-900">
                        Search Results ({searchResults.length})
                      </h3>
                      {searchResults.map((result) => (
                        <div key={result.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              {result.metadata?.category && (
                                <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(result.metadata.category)}`}>
                                  {result.metadata.category}
                                </span>
                              )}
                            </div>
                            {result.similarity_score && (
                              <span className="text-xs text-gray-500">
                                {Math.round(result.similarity_score * 100)}% match
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {result.content.length > 200 
                              ? `${result.content.substring(0, 200)}...` 
                              : result.content
                            }
                          </p>
                          {result.metadata?.type && (
                            <p className="text-xs text-gray-500 mt-2">
                              Type: {result.metadata.type} | Source: {result.metadata.source || 'unknown'}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Clear Data Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Trash2 className="w-5 h-5 mr-2 text-red-600" />
                Clear Data
              </h2>

              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-red-800 mb-1">Danger Zone</h3>
                      <p className="text-sm text-red-700">
                        This will permanently delete all GMCT knowledge chunks from the database. 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleClearData}
                  disabled={isClearing}
                  className="bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isClearing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Clearing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5 mr-2" />
                      Clear All GMCT Data
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Development Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-800 mb-2">Development Notes</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Import processes knowledge chunks with embeddings (varies by file size)</li>
                <li>• Search uses text-based matching (semantic search via API)</li>
                <li>• All operations are scoped to GMCT cemetery (cemetery_id: 'gmct')</li>
                <li>• Embeddings are generated using OpenAI text-embedding-3-small</li>
                <li>• Uploaded files must have url, title, content, timestamp fields</li>
                <li>• FAQ chunks are automatically added to uploaded data</li>
                <li>• Maximum file size: 10MB</li>
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom padding to ensure content is accessible */}
      <div className="pb-32">
      </div>
    </div>
  );
};

export default AdminScreen;